<script lang="ts" setup>
import api from "@/modules/api";
import { useGlobalUser } from "@/stores/globalUser";
import { ResponseError } from "@/types/api-types";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const user = useGlobalUser();

const showBind = ref(false);
const isPhoneLogin = ref(false);
const phone = ref("");
const email = ref("");

// Add new refs for delete account popup
const showDeleteConfirm = ref(false);
const deleteVerification = ref("");

const EMAIL_REG: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REG: RegExp = /^1[3-9]\d{9}$/;
const RESEND_INTERVAL_MS = 60;
const showCodeInput = ref(false);
const nextResendTime = ref(0);
const code = ref(["", "", "", ""]);

const isSendingCode = ref(false);

const formRef = ref();

const clickBindEmail = () => {
  isPhoneLogin.value = false;
  showBind.value = true;
};

const clickBindPhone = () => {
  isPhoneLogin.value = true;
  showBind.value = true;
};

const onSubmit = async () => {
  if (!isPhoneLogin.value) {
    await api.bindEmail({
      email: email.value,
      code: Number(code.value.join("")),
    });
    user.user.email = email.value;
  } else {
    await api.bindPhone({
      phone: phone.value,
      code: Number(code.value.join("")),
    });
    user.user.phone = phone.value;
  }
  showBind.value = false;
  showSuccessToast(t("Message.bindSuccess"));
};

const sendVerificationCode = async () => {
  isSendingCode.value = true;
  try {
    if (!isPhoneLogin.value) {
      await api.createOneVerification({ email: email.value, type: 2 });
    } else {
      await api.createOneVerification({ phone: phone.value, type: 2 });
    }
    nextResendTime.value = Date.now() + RESEND_INTERVAL_MS * 1000;
    showCodeInput.value = true;
  } catch (e) {
    showFailToast((e as ResponseError).message);
    throw e;
  } finally {
    isSendingCode.value = false;
  }
};

const onCodeInput = (e: InputEvent, index: number) => {
  const target = e.target as HTMLInputElement;
  const value = target.value;
  // 1. 输入单个字符
  if (value.length === 1) {
    code.value[index] = value;
    focusNext(index + 1);
  } else if (value.length > 1) {
    // 2. 输入多个字符(粘贴)
    const maxlength = Math.min(4 - index, value.length);
    for (let i = 0; i < maxlength; i++) {
      code.value[index + i] = value[i];
    }
    focusNext(index + (maxlength - 1));
  } else {
    // 3. 删除字符
    code.value[index] = "";
    focusPrev(index - 1);
  }
};

const codeRefs = ref<HTMLInputElement[]>([]);
function focusNext(next: number) {
  if (codeRefs.value[next] && code.value[next - 1] !== "") {
    codeRefs.value[next].focus();
  }
}

function focusPrev(prev: number) {
  if (codeRefs.value[prev] && code.value[prev + 1] === "") {
    codeRefs.value[prev].focus();
  }
}

const clickDeleteAccount = () => {
  // showConfirmDialog({
  //   message: t("ConfirmModal.deleteAccountTip"),
  //   confirmButtonText: t("Buttons.delete"),
  //   cancelButtonText: t("Buttons.cancel"),
  //   confirmButtonColor: "error",
  // }).then(() => {
  showDeleteConfirm.value = true;
  // });
};

const confirmDeleteAccount = async () => {
  // const verifyValue = user.user.email || user.user.phone;
  if (
    deleteVerification.value !== user.user.email &&
    deleteVerification.value !== user.user.phone
  )
    return showFailToast(t("Message.verificationFailed"));

  const loading = showLoadingToast(t("Message.deletingAccount"));
  await user.deleteUser();
  setTimeout(() => {
    loading.close();
    showDeleteConfirm.value = false;
    showSuccessToast(t("Message.deleteAccountSuccess"));
  }, 200);
};
</script>
<template>
  <div class="lg:pt-[var(--top-nav-height)] h-full flex flex-col">
    <ThreadsNav :show-center="false" class="lg:hidden" show-back />
    <div class="flex flex-col gap-4 text-sm flex-1">
      <div class="px-4 font-bold text-lg">
        {{ $t("setting.accountInfo") }}
      </div>
      <div class="flex-1">
        <VanCellGroup inset>
          <VanCell
            :title="$t('setting.account')"
            :value="String(user.user.id).padStart(5, '0')"
          />
          <VanCell :title="$t('setting.email')">
            <template #value>
              <div v-if="user.user.email">{{ user.user.email }}</div>
              <button v-else class="btn btn-xs" @click="clickBindEmail">
                {{ $t("setting.bindEmail") }}
              </button>
            </template>
          </VanCell>
          <VanCell :title="$t('setting.phone')">
            <template #value>
              <div v-if="user.user.phone">{{ user.user.phone }}</div>
              <button v-else class="btn btn-xs" @click="clickBindPhone">
                {{ $t("setting.bindPhone") }}
              </button>
            </template>
          </VanCell>
        </VanCellGroup>
      </div>
      <div class="flex justify-center pb-5">
        <button
          class="btn btn-error btn-outline btn-sm"
          @click="clickDeleteAccount"
        >
          {{ $t("Buttons.deleteAccount") }}
        </button>
      </div>
    </div>

    <VanPopup v-model:show="showBind" class="p-4 rounded" position="bottom">
      <VanForm ref="formRef" class="flex flex-col gap-2 flex-grow w-full">
        <template v-if="!isPhoneLogin">
          <div class="flex justify-between items-center">
            <label class="text-base">
              {{ $t("loginmodal.email") }}
            </label>
          </div>
          <VanField
            v-model="email"
            :disabled="isSendingCode"
            :placeholder="$t('loginmodal.emailplaceholder')"
            :rules="[
              {
                required: true,
                message: $t('loginmodal.emailplaceholderCorrectly'),
                pattern: EMAIL_REG,
              },
            ]"
            class="bg-transparent border-none input-field login-input"
            type="email"
          />
        </template>
        <template v-else>
          <div class="flex justify-between items-center">
            <label class="text-base">
              {{ $t("loginmodal.phone") }}
            </label>
          </div>
          <VanField
            v-model="phone"
            :disabled="isSendingCode"
            :placeholder="$t('loginmodal.phoneplaceholder')"
            :rules="[
              {
                required: true,
                message: $t('loginmodal.phoneplaceholderCorrectly'),
                pattern: PHONE_REG,
              },
            ]"
            class="bg-transparent border-none input-field login-input"
            type="tel"
          />
        </template>
        <div class="flex flex-col gap-3">
          <button
            v-if="nextResendTime === 0"
            :disabled="
              isSendingCode ||
              (isPhoneLogin && phone.length === 0) ||
              (!isPhoneLogin && email.length === 0)
            "
            class="btn btn-ghost"
            @click="sendVerificationCode"
          >
            {{ $t("loginmodal.sendcode") }}
          </button>
          <button v-else class="btn" disabled type="button">
            {{ $t("loginmodal.resendHit") }}
            <VanCountDown
              :time="nextResendTime - Date.now()"
              class="inline-block text-inherit"
              format="ss"
              @finish="nextResendTime = 0"
            />
            {{ $t("loginmodal.resendHit2") }}
          </button>
          <template v-if="showCodeInput">
            <div class="grid grid-cols-4 gap-4 grid-rows-1">
              <input
                v-for="i in code.length"
                :key="i"
                ref="codeRefs"
                :value="code[i - 1]"
                class="border bg-transparent h-14 text-base-content rounded text-center p-0 text-2xl border-base-content/20"
                maxlength="1"
                type="number"
                @input="onCodeInput($event as InputEvent, i - 1)"
                @keydown.delete="focusPrev(i - 2)"
              />
            </div>
            <button class="btn btn-primary" @click="onSubmit">
              {{ $t("Buttons.verify") }}
            </button>
          </template>
        </div>
      </VanForm>
    </VanPopup>

    <VanPopup
      v-model:show="showDeleteConfirm"
      class="p-4 rounded"
      position="bottom"
    >
      <div class="flex flex-col gap-4">
        <div class="text-lg font-bold">
          {{ $t("ConfirmModal.deleteAccountTip") }}
        </div>
        <div class="text-sm">
          {{ $t("ConfirmModal.inputEmailOrPhoneToConfirm") }}
        </div>
        <VanField
          v-model="deleteVerification"
          :placeholder="user.user.email ? user.user.email : user.user.phone"
          class="bg-transparent border-none input-field"
        />
        <div class="flex justify-between gap-2">
          <button
            class="btn btn-outline flex-1"
            @click="showDeleteConfirm = false"
          >
            {{ $t("Buttons.cancel") }}
          </button>
          <button class="btn btn-error flex-1" @click="confirmDeleteAccount">
            {{ $t("Buttons.delete") }}
          </button>
        </div>
      </div>
    </VanPopup>
  </div>
</template>
